import {
	buildStudentNameIndex,
	isStudentNameIndex,
	MIN_COMPACT_MATCH_LENGTH,
	MIN_UNIQUE_FIRST_LENGTH,
	normalizeName,
	compactName,
} from './buildStudentNameIndex'

const FUZZY_SIMILARITY_THRESHOLD = 0.86

function escapeRegExp(value) {
	return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function hasWordBoundaryPhrase(normalizedTranscript, phrase) {
	if (!phrase) return false
	const pattern = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(phrase)}(?:[^a-z0-9]|$)`)
	return pattern.test(normalizedTranscript)
}

function levenshteinDistance(a, b) {
	if (a === b) return 0
	if (!a.length) return b.length
	if (!b.length) return a.length

	const rows = a.length + 1
	const cols = b.length + 1
	const matrix = Array.from({ length: rows }, () => new Array(cols).fill(0))

	for (let i = 0; i < rows; i += 1) matrix[i][0] = i
	for (let j = 0; j < cols; j += 1) matrix[0][j] = j

	for (let i = 1; i < rows; i += 1) {
		for (let j = 1; j < cols; j += 1) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1
			matrix[i][j] = Math.min(
				matrix[i - 1][j] + 1,
				matrix[i][j - 1] + 1,
				matrix[i - 1][j - 1] + cost,
			)
		}
	}

	return matrix[a.length][b.length]
}

function similarityRatio(a, b) {
	if (!a && !b) return 1
	if (!a || !b) return 0
	const maxLen = Math.max(a.length, b.length)
	if (!maxLen) return 1
	return 1 - levenshteinDistance(a, b) / maxLen
}

/**
 * Handles STT spacing variants like "Al Rashid" vs roster "AlRashid".
 * Tries nearby window sizes and compares both spaced and compact forms.
 */
function matchFuzzyFullName(tokens, entry) {
	if (entry.tokenCount < 2 && entry.fullCompact.length < MIN_COMPACT_MATCH_LENGTH) {
		return false
	}

	const minWindow = Math.max(2, entry.tokenCount - 1)
	const maxWindow = Math.min(tokens.length, entry.tokenCount + 2)
	if (tokens.length < minWindow) return false

	for (let windowSize = minWindow; windowSize <= maxWindow; windowSize += 1) {
		for (let i = 0; i <= tokens.length - windowSize; i += 1) {
			const windowPhrase = tokens.slice(i, i + windowSize).join(' ')
			const windowCompact = compactName(windowPhrase)
			if (
				similarityRatio(windowPhrase, entry.fullNorm) >= FUZZY_SIMILARITY_THRESHOLD ||
				(entry.fullCompact &&
					similarityRatio(windowCompact, entry.fullCompact) >=
						FUZZY_SIMILARITY_THRESHOLD)
			) {
				return true
			}
		}
	}
	return false
}

/**
 * Finds a student mentioned in the transcript.
 *
 * Tiers:
 * - A: full name as a word-boundary phrase, or space-insensitive compact match
 *   (e.g. roster "Saad AlRashid" vs STT "Saad Al Rashid")
 * - C: unique first name (length >= 4) as a word-boundary token
 * - B (optional): fuzzy full-name window match for STT typos / spacing
 *
 * Ambiguous first-name hits are ignored. Duplicate full names resolve to the
 * first student in roster/index order.
 *
 * Profile-initiated recordings bypass this helper via assignStudent + lock.
 */
export function matchStudentFromTranscript(
	transcript,
	studentsOrIndex = [],
	options = {},
) {
	const { allowFuzzy = false } = options
	const normalizedTranscript = normalizeName(transcript)
	if (!normalizedTranscript) return ''

	const index = isStudentNameIndex(studentsOrIndex)
		? studentsOrIndex
		: buildStudentNameIndex(studentsOrIndex)

	const { entries, firstNameToIds } = index
	if (!entries.length) return ''

	const compactTranscript = compactName(normalizedTranscript)

	for (const entry of entries) {
		if (hasWordBoundaryPhrase(normalizedTranscript, entry.fullNorm)) {
			return entry.id
		}
		// "AlRashid" in DB vs "Al Rashid" in STT (and similar compound surnames)
		if (
			entry.fullCompact.length >= MIN_COMPACT_MATCH_LENGTH &&
			compactTranscript.includes(entry.fullCompact)
		) {
			return entry.id
		}
	}

	for (const entry of entries) {
		if (entry.firstNorm.length < MIN_UNIQUE_FIRST_LENGTH) continue
		const ids = firstNameToIds.get(entry.firstNorm) || []
		if (ids.length !== 1) continue
		if (hasWordBoundaryPhrase(normalizedTranscript, entry.firstNorm)) {
			return entry.id
		}
	}

	if (allowFuzzy) {
		const tokens = normalizedTranscript.split(' ')
		for (const entry of entries) {
			if (matchFuzzyFullName(tokens, entry)) {
				return entry.id
			}
		}
	}

	return ''
}

export { buildStudentNameIndex, isStudentNameIndex } from './buildStudentNameIndex'
